/**
 * audioPlayback.ts
 *
 * TTS / announcement audio playback service for Otto Touch Panel.
 *
 * Supports two priority queues:
 *  - 'normal'  : background audio, queued, plays sequentially
 *  - 'urgent'  : interrupts current playback and clears the normal queue
 *
 * Audio is fetched from a URL and decoded via the Web Audio API.
 */

type Priority = 'normal' | 'urgent';

interface PlaybackItem {
  url: string;
  priority: Priority;
  resolve: () => void;
  reject: (err: unknown) => void;
}

let audioCtx: AudioContext | null = null;
let currentSource: AudioBufferSourceNode | null = null;
let normalQueue: PlaybackItem[] = [];
let playing = false;
let masterVolume = 1.0; // 0–1

function ensureContext(): AudioContext {
  if (!audioCtx || audioCtx.state === 'closed') {
    audioCtx = new AudioContext();
  }
  return audioCtx;
}

/** Set master volume (0–1). Applies to the next playback. */
export function setVolume(level: number): void {
  masterVolume = Math.max(0, Math.min(1, level));
}

/** Get current master volume. */
export function getVolume(): number {
  return masterVolume;
}

/** Whether audio is currently playing. */
export function isPlaying(): boolean {
  return playing;
}

/**
 * Play audio from a URL.
 * @param url  - HTTP URL of the audio file (WAV, OGG, MP3)
 * @param priority - 'urgent' immediately stops current audio and clears queue
 * @returns Promise that resolves when playback finishes
 */
export function playAudioUrl(url: string, priority: Priority = 'normal'): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const item: PlaybackItem = { url, priority, resolve, reject };

    if (priority === 'urgent') {
      // Interrupt current playback
      stopCurrent();
      // Clear normal queue and reject pending items
      normalQueue.forEach((q) => q.reject(new Error('Interrupted by urgent audio')));
      normalQueue = [];
      normalQueue.unshift(item);
    } else {
      normalQueue.push(item);
    }

    if (!playing) {
      processQueue();
    }
  });
}

/** Stop all playback immediately. */
export function stopAll(): void {
  stopCurrent();
  normalQueue.forEach((q) => q.reject(new Error('Stopped')));
  normalQueue = [];
  playing = false;
}

// ─── Private helpers ──────────────────────────────────────────────────────────

function stopCurrent(): void {
  if (currentSource) {
    try {
      currentSource.onended = null;
      currentSource.stop();
    } catch {
      // Already stopped
    }
    currentSource = null;
  }
}

async function processQueue(): Promise<void> {
  if (playing || normalQueue.length === 0) return;

  const item = normalQueue.shift()!;
  playing = true;

  try {
    const ctx = ensureContext();

    // Resume context if suspended (browser autoplay policy)
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }

    const response = await fetch(item.url);
    if (!response.ok) throw new Error(`Audio fetch failed: ${response.status}`);
    const arrayBuffer = await response.arrayBuffer();
    const audioBuffer = await ctx.decodeAudioData(arrayBuffer);

    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;

    // Volume node
    const gainNode = ctx.createGain();
    gainNode.gain.value = masterVolume;

    source.connect(gainNode);
    gainNode.connect(ctx.destination);

    currentSource = source;

    await new Promise<void>((res) => {
      source.onended = () => {
        currentSource = null;
        res();
      };
      // Start playback
      source.start(0);
    });

    playing = false;
    item.resolve();
  } catch (err) {
    playing = false;
    currentSource = null;
    item.reject(err);
  }

  // Continue queue
  if (normalQueue.length > 0) {
    processQueue();
  }
}
